"use client"

import { createContext, useState, useEffect, useContext } from "react"
import { useAuth } from "./AuthContext"

// const url = "http://localhost:5500/api"
const url = "/api"


// Create context
const AppContext = createContext()

// Provider component
export const AppProvider = ({ children }) => {
  const [tags, setTags] = useState([])
  const [shifts, setShifts] = useState([])
  const { currentUser } = useAuth()
  const [loadingApp, setLoading] = useState(true)


  const fetchTags = async () => {
    try {
      setLoading(true)
      const response = await (await fetch(`${url}/tags/gettags`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${currentUser.token}`,
      },
    })).json()

    if (response.error) {
    } else {
      setTags(response.tags)
      setShifts(response.shifts)
      setLoading(false)
      localStorage.setItem("tags", JSON.stringify(response.tags))
      localStorage.setItem("shifts", JSON.stringify(response.shifts))
    }
  } catch (err) {
    setTags(JSON.parse(localStorage.getItem("tags")))
    setShifts(JSON.parse(localStorage.getItem("shifts")))
    setLoading(false)
  }
  }

  // Load data from localStorage on mount or when user changes
  useEffect(() => {
    if (currentUser) {
      fetchTags()

    } else {
      // Clear data when no user is logged in
      setTags([])
      setShifts([])
    }
  }, [currentUser])

  // Save data to localStorage whenever it changes
  useEffect(() => {
    if (currentUser) {
      const userId = currentUser.id
      localStorage.setItem(`tags_${userId}`, JSON.stringify(tags))
    }
  }, [tags, currentUser])

  useEffect(() => {
    if (currentUser) {
      const userId = currentUser.id
      localStorage.setItem(`shifts_${userId}`, JSON.stringify(shifts))
    }
  }, [shifts, currentUser])

  // Tag functions
  const addTag = async (tag) => {
    setLoading(true)
    const newTag = {
      ...tag,
      id: Date.now().toString(),
    }

    const response = await (await fetch(`${url}/tags/addtag`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${currentUser.token}`,
      },
      body: JSON.stringify({ tag: newTag }),
    })).json()

    if (response.error) {
      alert(response.msg)
    } else {
      setTags(response.tags)
      setLoading(false)
      return newTag
    }
  }

  const updateTag = async (id, updatedTag) => {
    setLoading(true)
    const response = await (await fetch(`${url}/tags/updatetag`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${currentUser.token}`,
      },
      body: JSON.stringify({ tag: { id, updatedTag } }),
    })).json()

    if (response.error) {
    } else {
      setTags(response.tags)
      setLoading(false)
      return updatedTag
    }
  }

  const deleteTag = async (id) => {
    setLoading(true)
    const response = await (await fetch(`${url}/tags/deletetag`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${currentUser.token}`,
      },
      body: JSON.stringify({ tag: { id } }),
    })).json()

    if (response.error) {
      alert(response.msg)
    } else {
      setTags(response.tags)
      setLoading(false)

    }

    // setTags(tags.filter((tag) => tag.id !== id))
    // Also delete all shifts with this tag
    setShifts(shifts.filter((shift) => shift.tagId !== id))
  }

  const getTag = (id) => {
    return tags.find((tag) => tag.id === id) || null
  }

  // Shift functions
  const addShift = async (shift) => {
    setLoading(true)
    const response = await (await fetch(`${url}/shifts/addshift`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${currentUser.token}`,
      },
      body: JSON.stringify({ shift }),
    })).json()
    if (response.error) {
    } else {
      setShifts(response.shifts)
    setLoading(false)

    }
  }

  const updateShift = async (id, updatedShift) => {
    setLoading(true)
    const response = await (await fetch(`${url}/shifts/updateshift`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${currentUser.token}`,
      },
      body: JSON.stringify({ shift: { id, updatedShift } }),
    })).json()

    if (response.error) {
      alert(response.msg)
    } else {
      setShifts(response.shifts)
    setLoading(false)
    return updatedShift
    }
  }

  const deleteShift = async (id) => {
    setLoading(true)

    const response = await (await fetch(`${url}/shifts/deleteshift`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${currentUser.token}`,
      },
      body: JSON.stringify({ shift: { id } }),
    })).json()

    if (response.error) {
      alert(response.msg)
    } else {
      setShifts(response.shifts)
      setLoading(false)
    }

    // setShifts(shifts.filter((shift) => shift.id !== id))
  }

  const markShiftAsPaid = async (id) => {
    setLoading(true)
    
    const response = await (await fetch(`${url}/shifts/markpaid`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${currentUser.token}`,
      },
      body: JSON.stringify({ shift: { id } }),
    })).json()

    if (response.error) {
      alert(response.msg)
    } else {
    setLoading(false)
    setShifts(response.shifts)
    }

    // setShifts(shifts.map((shift) => (shift.id === id ? { ...shift, paid: true } : shift)))
  }

  // Get shifts for a specific date
  const getShiftsForDate = (date) => {
    return shifts.filter((shift) => shift.date === date)
  }

  // Get shifts for a specific month and year
  const getShiftsForMonth = (month, year) => {
    return shifts.filter((shift) => {
      const date = shift.date.split("-")
      const shiftYear = parseInt(date[0], 10)
      const shiftMonth = parseInt(date[1], 10) - 1 // Months are zero-indexed in JavaScript

      const shiftDate = new Date(shift.date)
      return month === shiftMonth && shiftYear === year
    })
  }

  return (
    <AppContext.Provider
      value={{
        tags,
        shifts,
        addTag,
        updateTag,
        deleteTag,
        getTag,
        addShift,
        updateShift,
        deleteShift,
        markShiftAsPaid,
        getShiftsForDate,
        getShiftsForMonth,
        loadingApp
      }}
    >
      {children}
    </AppContext.Provider>
  )
}

// Custom hook to use the context
export const useAppContext = () => {
  const context = useContext(AppContext)
  if (!context) {
    throw new Error("useAppContext must be used within an AppProvider")
  }
  return context
}

